import { Injectable, NgZone } from '@angular/core';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ComponentBridgeService {
  constructor(private ngZone: NgZone) {}

  /**
   * Exposes any component method to window.ERP
   * @param methodName - Name to expose in window.ERP (e.g., 'updatePOStatus')
   * @param componentMethod - The actual component method
   * @param componentContext - 'this' context of the component
   */
  exposeMethod(
    methodName: string,
    componentMethod: Function,
    componentContext: any
  ): void {
    const w = window as any;
    w.ERP = w.ERP || {};

    w.ERP[methodName] = (...args: any[]) => {
      return new Observable(observer => {
        this.ngZone.run(() => {
          const result = componentMethod.apply(componentContext, args);
          
          if (result && typeof result.subscribe === 'function') {
            // It's an Observable
            result.subscribe({
              next: (res: any) => {
                observer.next(res);
                observer.complete();
              },
              error: (err: any) => observer.error(err)
            });
          } else {
            // Direct value
            observer.next(result);
            observer.complete();
          }
        });
      });
    };

    console.log(`✅ Exposed method: window.ERP.${methodName}`);
  }

  /**
   * Expose multiple methods at once
   */
  exposeMethods(methods: { name: string; fn: Function; context: any }[]): void {
    methods.forEach(({ name, fn, context }) => {
      this.exposeMethod(name, fn, context);
    });
    
    window.dispatchEvent(new Event('ERP_READY'));
  }

  /**
   * Exposes static data (like environment variables) to window.ERP
   * @param data - Object containing data to expose
   * @example
   * componentBridge.exposeData({
   *   apiURL: environment.apiURL,
   *   production: environment.production
   * });
   * // Access via: window.ERP.apiURL or window.ERP.production
   */
  exposeData(data: Record<string, any>): void {
    const w = window as any;
    w.ERP = w.ERP || {};
    
    Object.keys(data).forEach(key => {
      w.ERP[key] = data[key];
      console.log(`✅ Exposed data: window.ERP.${key}`);
    });
  }
}
